<template>
  <div>
    <SearchBase ref="searchBaseRef" @handleSearch="handleSearch" />

    <BaseTable
      :headers="headers"
      :desserts="desserts"
      :total-size="totalSize"
      :list-height="555"
      :items-per-page="pageSize"
      :loading="loading"
      :page-size="pageSize"
      :current-page="currentPage"
      @pageSizeChange="handleUpdate"
      @currentPageChange="handleCurrentPageChange"
    >
      <template #[`item.reportType`]="{ item }">
        {{ getTextById(downloadList, item.reportType) }}
      </template>

      <template #[`item.jobStatus`]="{ item }">
        <span class="status-btn" :class="getStatusClass(item.jobStatus)">
          {{ getTextById(jobStatusList, item.jobStatus) }}
        </span>
      </template>

      <template #[`item.operation`]="{ item }">
        <span
          v-if="item.jobStatus === 2 && !isDownloading(item.id)"
          class="color-blue fw-6 cursor"
          @click="handleAsyncDownload(item)"
        >
          下载
        </span>
        <span
          v-else-if="item.jobStatus === 2 && isDownloading(item.id)"
          class="fw-6 color-blue download-loading"
        >
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            color="primary"
            class="mr-1"
          />
          {{ getDownloadStatus(item.id) }}
        </span>
        <span v-else class="fw-6 color-disable">下载</span>
        <span
          class="fw-6 color-warn cursor ml-3"
          @click="handleDeleteJob(item)"
        >
          删除
        </span>
      </template>
    </BaseTable>
    <BaseDialog
      :visible.sync="deleteDialogVisible"
      content="确认删除该下载记录？"
      :is-warn="true"
      @handleConfirm="confirmDeleteJob"
    />
  </div>
</template>

<script>
import usePagination from '@/hooks/usePagination'
import { reactive, ref, watch, onBeforeUnmount } from 'vue'
import BaseTable from '@/components/BaseTable.vue'
import BaseDialog from '@/components/BaseDialog.vue'
import downloadService from '@/request/api/download'
import snackbar from '@/utils/snackbar'
import {
  getTextById,
  toThousands,
  getTimeStampByString,
  formatDateByTimeStamp,
} from '@/utils/index'
import { downloadList } from '@/request/api/common'
import useDownload from '@/hooks/useDownload'
import SearchBase from './SearchBase.vue'

export default {
  name: 'DownloadList',
  components: {
    SearchBase,
    BaseTable,
    BaseDialog,
  },
  props: {
    dialogVisible: {
      type: Boolean,
      default: false,
    },
  },
  emits: [],
  setup(props) {
    const loading = ref(false)
    const downloadingJobs = ref({}) // Track multiple downloading jobs: { itemId: { jobId, status, progress } }
    const pollingTimers = ref({}) // Track polling timers

    const {
      pageSize,
      currentPage,
      totalSize,
      handleUpdate,
      handleCurrentPageChange,
    } = usePagination()

    // 表头
    const headers = ref([
      {
        text: '文件名称',
        align: 'left',
        width: '500',
        sortable: false,
        value: 'fileKey',
      },
      {
        text: '报表类型',
        value: 'reportType',
        sortable: false,
        align: 'left',
      },
      {
        text: '数据量',
        value: 'totalCount',
        align: 'right',
        sortable: false,
      },
      {
        text: '启动时间',
        value: 'startTime',
        align: 'left',
        sortable: false,
      },
      {
        text: '生成时间',
        value: 'endTime',
        align: 'left',
        sortable: false,
      },
      {
        text: '状态',
        value: 'jobStatus',
        align: 'center',
        sortable: false,
      },
      {
        text: '下载次数',
        value: 'downloadTimes',
        align: 'right',
        sortable: false,
      },
      { text: '操作', value: 'operation', sortable: false },
    ])

    // 表格数据
    const desserts = ref([])
    const jobStatusList = ref([
      { text: '处理中', value: 0 },
      {
        text: '处理中',
        value: 1,
      },
      {
        text: '已生成',
        value: 2,
      },
      {
        text: '失败',
        value: 3,
      },
    ])

    const getStatusClass = (status) => {
      switch (status) {
        case 0:
          return 'status-btn__info'
        case 1:
          return 'status-btn__info'
        case 2:
          return 'status-btn__success'
        case 3:
          return 'status-btn__warn'
        default:
          return 'status-btn__info'
      }
    }

    const formState = reactive({
      data: {},
    })

    const getParams = () => {
      let startTime = null
      let endTime = null
      if (formState.data.date?.length) {
        const [startTimeStamp, endTimeStamp] = formState.data.date

        startTime = getTimeStampByString(startTimeStamp)
        if (!endTimeStamp || endTimeStamp === startTimeStamp) {
          endTime = startTime + 86399999
        } else {
          endTime = getTimeStampByString(endTimeStamp) + 86399999
        }
      }

      return {
        ...formState.data,
        startTime,
        endTime,
        pageNum: currentPage.value,
        pageSize: pageSize.value,
      }
    }

    // 获取表格数据
    const getList = async (silent = false) => {
      if (!silent) loading.value = true
      const params = getParams()
      delete params.date
      try {
        const { dataList, total } = await downloadService.getDownloadList(
          params
        )

        totalSize.value = Number(total)
        desserts.value = dataList.map((item) => ({
          ...item,
          startTime: item.startTime
            ? formatDateByTimeStamp(item.startTime)
            : '-',
          endTime: item.endTime ? formatDateByTimeStamp(item.endTime) : '-',
        }))
      } catch (err) {
        console.log(err)
      } finally {
        loading.value = false
      }
    }

    getList()

    // Auto-poll when there are in-progress jobs (status 0 or 1)
    const listPollingTimer = ref(null)
    const startListPolling = () => {
      if (listPollingTimer.value) return
      listPollingTimer.value = setInterval(async () => {
        await getList(true)
        const hasPending = desserts.value.some(
          (d) => d.jobStatus === 0 || d.jobStatus === 1
        )
        if (!hasPending) {
          clearInterval(listPollingTimer.value)
          listPollingTimer.value = null
        }
      }, 5000)
    }
    const stopListPolling = () => {
      if (listPollingTimer.value) {
        clearInterval(listPollingTimer.value)
        listPollingTimer.value = null
      }
    }
    // Start polling after initial load if needed
    watch(
      desserts,
      (list) => {
        const hasPending = list.some(
          (d) => d.jobStatus === 0 || d.jobStatus === 1
        )
        if (hasPending) {
          startListPolling()
        }
      },
      { immediate: true }
    )

    const handleSearch = (data) => {
      formState.data = { ...data }
      if (currentPage.value === 1) getList()
      currentPage.value = 1
    }

    watch([pageSize, currentPage], () => {
      getList()
    })

    watch(
      () => props.dialogVisible,
      (newVal) => {
        if (newVal) {
          getList()
        }
      }
    )

    const { download } = useDownload()

    const addCount = async (item) => {
      await downloadService.addCount({
        id: item.id,
      })
      getList()
    }

    // Check if item is currently downloading
    const isDownloading = (itemId) => {
      return !!downloadingJobs.value[itemId]
    }

    // Get download status text
    const getDownloadStatus = (itemId) => {
      const job = downloadingJobs.value[itemId]
      if (!job) return '下载中...'

      switch (job.status) {
        case 'PENDING':
          return '准备中...'
        case 'PROCESSING':
          return `处理中 ${job.progress || 0}%...`
        case 'DOWNLOADING':
          return '下载中...'
        default:
          return '下载中...'
      }
    }
    // Stop polling for a specific item
    const stopPolling = (itemId) => {
      if (pollingTimers.value[itemId]) {
        clearInterval(pollingTimers.value[itemId])
        delete pollingTimers.value[itemId]
      }
    }

    // Download completed file
    const downloadCompletedFile = async (itemId, jobId) => {
      try {
        downloadingJobs.value = {
          ...downloadingJobs.value,
          [itemId]: { ...downloadingJobs.value[itemId], status: 'DOWNLOADING' },
        }

        const response = await downloadService.downloadWatermarkedFile(jobId)

        // Extract filename from Content-Disposition header
        const cd = response.headers['content-disposition']
        const blob = response.data

        // Create object URL from blob
        const href = window.URL.createObjectURL(blob)

        // Trigger download
        download(href, cd)

        // Cleanup object URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(href)
        }, 100)

        // Find the item and increment count
        const item = desserts.value.find((d) => d.id === itemId)
        if (item) {
          addCount(item)
        }

        snackbar.success('下载成功')
      } catch (error) {
        console.error('Download error:', error)
        snackbar.error('下载失败，请稍后重试')
      } finally {
        // Cleanup
        const newJobs = { ...downloadingJobs.value }
        delete newJobs[itemId]
        downloadingJobs.value = newJobs
      }
    }

    // Poll job status
    const pollJobStatus = async (itemId, jobId) => {
      try {
        const response = await downloadService.getWatermarkJobStatus(jobId)

        if (response.errorMessage) {
          throw new Error(response.errorMessage || 'Failed to get job status')
        }

        const jobData = response
        // Update job info
        downloadingJobs.value = {
          ...downloadingJobs.value,
          [itemId]: {
            jobId,
            status: jobData.status,
            progress: jobData.progress || 0,
          },
        }

        if (jobData.status === 'COMPLETED') {
          // Stop polling
          stopPolling(itemId)

          // Download the file
          await downloadCompletedFile(itemId, jobId)
        } else if (jobData.status === 'FAILED') {
          // Stop polling
          stopPolling(itemId)

          // Show error
          snackbar.error(`水印处理失败: ${jobData.errorMessage || '未知错误'}`)
          const newJobs1 = { ...downloadingJobs.value }
          delete newJobs1[itemId]
          downloadingJobs.value = newJobs1
        } else if (jobData.status === 'NOT_FOUND') {
          // Stop polling
          stopPolling(itemId)

          snackbar.error('任务未找到或已过期')
          const newJobs2 = { ...downloadingJobs.value }
          delete newJobs2[itemId]
          downloadingJobs.value = newJobs2
        }
        // Otherwise, continue polling (PENDING or PROCESSING)
      } catch (error) {
        console.error('Error polling job status:', error)
        stopPolling(itemId)
        snackbar.error('获取处理状态失败，请稍后重试')
        const newJobs3 = { ...downloadingJobs.value }
        delete newJobs3[itemId]
        downloadingJobs.value = newJobs3
      }
    }
    // Handle async download
    const handleAsyncDownload = async (item) => {
      try {
        // Submit watermark job
        const response = await downloadService.submitWatermarkJob({
          id: item.id,
          fileKey: item.fileKey,
          bucketName: item.bucketName,
          jobName: item.jobName,
          downloadExpTimeMillis: 300000,
        })

        console.log('Submit job response:', response)
        if (response.errorMessage) {
          throw new Error(response.errorMessage || 'Failed to submit job')
        }

        const jobData = response
        const jobId = jobData.jobId

        // Track this download
        downloadingJobs.value = {
          ...downloadingJobs.value,
          [item.id]: {
            jobId,
            status: 'PENDING',
            progress: 0,
          },
        }

        snackbar.info('水印处理已开始，请稍候...')

        // Start polling (every 2 seconds)
        pollingTimers.value[item.id] = setInterval(() => {
          pollJobStatus(item.id, jobId)
        }, 2000)

        // Also poll immediately
        pollJobStatus(item.id, jobId)
      } catch (error) {
        console.error('Submit job error:', error)
        snackbar.error('提交任务失败，请稍后重试')
        const newJobs4 = { ...downloadingJobs.value }
        delete newJobs4[item.id]
        downloadingJobs.value = newJobs4
      }
    }

    // Cleanup on unmount
    onBeforeUnmount(() => {
      // Stop all polling timers
      stopListPolling()
      Object.keys(pollingTimers.value).forEach((itemId) => {
        stopPolling(itemId)
      })
    })

    const deleteDialogVisible = ref(false)
    const deleteTargetItem = ref(null)

    const handleDeleteJob = (item) => {
      deleteTargetItem.value = item
      deleteDialogVisible.value = true
    }

    const confirmDeleteJob = async () => {
      deleteDialogVisible.value = false
      const item = deleteTargetItem.value
      if (!item) return
      try {
        await downloadService.deleteJob({ id: item.id })
        snackbar.success('删除成功')
        getList()
      } catch (error) {
        console.error(error)
        snackbar.error('删除失败')
      }
    }

    return {
      pageSize,
      currentPage,
      totalSize,
      handleUpdate,
      handleCurrentPageChange,
      headers,
      desserts,
      getTextById,
      toThousands,
      loading,
      handleSearch,
      getList,
      downloadList,
      handleAsyncDownload,
      isDownloading,
      getDownloadStatus,
      downloadingJobs,
      formatDateByTimeStamp,
      jobStatusList,
      getStatusClass,
      handleDeleteJob,
      confirmDeleteJob,
      deleteDialogVisible,
    }
  },
}
</script>

<style lang="scss" scoped>
.fw-6 {
  font-weight: 600;
}

.max-48 {
  max-width: 480px;
}

.ellipsis {
  display: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-loading {
  display: inline-flex;
  align-items: center;
}
</style>
